import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ActionIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HomePage = ({ user }) => {
  const [ptoRequests, setPtoRequests] = useState([]);
  const [onboardingItems, setOnboardingItems] = useState([]);
  // Employees who started before the migration cutoff are grandfathered: the
  // backend marks every onboarding item complete for them, so we don't want
  // to nag them with "Complete: …" tasks even if the local data is stale.
  const [isPreMigration, setIsPreMigration] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [ptoBalance, setPtoBalance] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  // The employment-reference card used to ship as static JSX in the bundle;
  // it now comes from GET /api/handbook so a logged-out scraper of the SPA
  // never sees Drake's personal contact info. `null` while loading, `{}` if
  // the fetch fails (so the modal still renders with an apology rather than
  // crashing).
  const [referenceInfo, setReferenceInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ptoRes, onbRes, managerRes, policiesRes, handbookRes] = await Promise.allSettled([
          fetch(`${API_BASE}/api/pto/requests`, { credentials: 'include' }).then(r => r.ok ? r.json() : {}),
          fetch(`${API_BASE}/api/onboarding/items`, { credentials: 'include' }).then(r => r.ok ? r.json() : { items: [] }),
          fetch(`${API_BASE}/api/manager/pending-requests`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
          fetch(`${API_BASE}/api/pto/policies`, { credentials: 'include' }).then(r => r.ok ? r.json() : { policies: [] }),
          fetch(`${API_BASE}/api/handbook/employment_reference`, { credentials: 'include' }).then(r => r.ok ? r.json() : { entry: { content: {} } }),
        ]);
        const ptoData = ptoRes.status === 'fulfilled' ? ptoRes.value : {};
        setPtoRequests(Array.isArray(ptoData) ? ptoData : ptoData.requests || ptoData.ptoRequests || []);
        const rawBal = ptoData.ptoBalance ?? ptoData.balance;
        if (rawBal) setPtoBalance(rawBal);
        const onbData = onbRes.status === 'fulfilled' ? onbRes.value : { items: [] };
        setOnboardingItems(Array.isArray(onbData.items) ? onbData.items : []);
        setIsPreMigration(Boolean(onbData.is_pre_migration));
        setPendingApprovals(managerRes.status === 'fulfilled' ? (Array.isArray(managerRes.value) ? managerRes.value : managerRes.value.requests || []) : []);
        const policiesData = policiesRes.status === 'fulfilled' ? policiesRes.value : { policies: [] };
        setPolicies(Array.isArray(policiesData.policies) ? policiesData.policies : []);
        const handbookData = handbookRes.status === 'fulfilled' ? handbookRes.value : { entry: { content: {} } };
        setReferenceInfo(handbookData?.entry?.content || {});
      } catch (e) {
        console.error('HomePage fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Only greet by name when a real name is present on the loaded user object.
  // Falling back to the email handle or a placeholder risks showing stale/wrong
  // text before the user has loaded, so we drop the name entirely instead.
  const displayName = user?.name || user?.first_name || '';

  // /api/onboarding/items wraps completion state in item.progress, not on the
  // item itself, and pre-migration employees are exempt entirely.
  const itemStatus = (item) => item?.progress?.status ?? (item?.progress?.completed ? 'completed' : 'incomplete');
  const itemIsComplete = (item) => itemStatus(item) === 'completed';
  const itemIsApplicable = (item) => itemStatus(item) !== 'na';
  const incompleteOnboarding = isPreMigration
    ? []
    : onboardingItems.filter(item => itemIsApplicable(item) && !itemIsComplete(item));

  const pendingPto = ptoRequests.filter(r => r.status === 'pending');
  const approvedUpcoming = ptoRequests.filter(r => r.status === 'approved' && new Date(r.start_date) >= new Date());

  const actionItems = [];

  if (pendingApprovals.length > 0) {
    pendingApprovals.forEach(req => {
      actionItems.push({
        title: `Approve Time Off Request: ${req.employee_name || req.employee_email}`,
        subtitle: `${req.leave_type || 'PTO'} \u00b7 ${req.start_date} to ${req.end_date}`,
      });
    });
  }

  if (incompleteOnboarding.length > 0) {
    incompleteOnboarding.slice(0, 3).forEach(item => {
      actionItems.push({
        title: `Complete: ${item.title || item.name}`,
        subtitle: item.description || 'Onboarding task',
      });
    });
  }

  const progressItems = [];

  if (pendingPto.length > 0) {
    pendingPto.forEach(req => {
      progressItems.push({
        title: `Time Off Request \u2013 ${req.leave_type || 'PTO'}`,
        subtitle: `${req.start_date} to ${req.end_date} \u00b7 Pending approval`,
      });
    });
  }

  // Suppress the onboarding progress line for pre-migration employees -- the
  // backend marks every item complete for them, so the line would either be
  // misleading or always say "All done".
  if (!isPreMigration && incompleteOnboarding.length > 0) {
    const applicable = onboardingItems.filter(itemIsApplicable);
    const completed = applicable.filter(itemIsComplete).length;
    progressItems.push({
      title: 'Onboarding Progress',
      subtitle: `${completed} of ${applicable.length} tasks completed`,
    });
  }

  // Parse a "YYYY-MM-DD" string as a local Date (avoids the UTC shift that
  // `new Date("2026-01-01")` causes in negative-offset timezones).
  const parseLocalDate = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).split('-').map(n => parseInt(n, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Holidays come from the policies the signed-in employee is actually
  // assigned to (the /api/pto/policies endpoint is already filtered by user).
  // Each policy.holidays entry is either a "YYYY-MM-DD" string or
  // { date, name }. Dedupe by date so two policies that share a holiday
  // (e.g. Christmas) don't render twice.
  const getUpcomingHolidays = () => {
    const seen = new Map();
    policies.forEach(policy => {
      (policy.holidays || []).forEach(h => {
        const dateStr = typeof h === 'string' ? h : h?.date;
        const name = typeof h === 'string' ? 'Holiday' : (h?.name || 'Holiday');
        const date = parseLocalDate(dateStr);
        if (!date || date < today) return;
        if (!seen.has(dateStr)) seen.set(dateStr, { date, name });
      });
    });
    return Array.from(seen.values()).map(({ date, name }) => ({
      date,
      title: name,
      subtitle: 'Public Holiday',
      isHoliday: true,
    }));
  };

  const importantDates = [];

  approvedUpcoming.forEach(req => {
    const date = parseLocalDate(req.start_date);
    if (!date) return;
    importantDates.push({
      date,
      title: req.employee_name || 'You',
      subtitle: `${req.leave_type || 'Paid Time Off'} \u00b7 ${req.start_date} to ${req.end_date}`,
    });
  });

  importantDates.push(...getUpcomingHolidays());

  // Real chronological sort (the previous version compared three-letter month
  // strings via localeCompare, which put APR before JAN).
  importantDates.sort((a, b) => a.date - b.date);

  // Cap to the next handful of dates so the card stays scannable.
  const visibleDates = importantDates.slice(0, 5).map(d => ({
    ...d,
    month: d.date.toLocaleString('default', { month: 'short' }).toUpperCase(),
    day: d.date.getDate(),
  }));

  if (loading) {
    return (
      <div style={{ padding: '60px 40px', textAlign: 'center', color: 'var(--symbolica-muted)', fontSize: '14px', letterSpacing: '0.05em' }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Banner */}
      <div className="home-banner">
        <img src="/banner.svg" alt="" className="home-banner-svg" />
      </div>

      {/* Greeting */}
      <div className="home-greeting">
        <h1 className="home-greeting-text">{getGreeting()}{displayName ? `, ${displayName}` : ''}</h1>
      </div>

      {/* Content Grid */}
      <div className="home-grid">
        {/* Left Column */}
        <div className="home-left">
          {/* Awaiting Your Action */}
          <div className="home-card">
            <h2 className="home-card-title">Awaiting Your Action</h2>
            {actionItems.length === 0 ? (
              <p className="home-empty">You're all caught up.</p>
            ) : (
              <div className="home-action-list">
                {actionItems.map((item, i) => (
                  <div className="home-action-item" key={i}>
                    <div className="home-action-icon"><ActionIcon /></div>
                    <div className="home-action-content">
                      <div className="home-action-title">{item.title}</div>
                      <div className="home-action-subtitle">{item.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Things in Progress */}
          <div className="home-card">
            <h2 className="home-card-title">Things in Progress</h2>
            {progressItems.length === 0 ? (
              <p className="home-empty">Nothing in progress right now.</p>
            ) : (
              <div className="home-action-list">
                {progressItems.map((item, i) => (
                  <div className="home-action-item" key={i}>
                    <div className="home-action-icon"><ClockIcon /></div>
                    <div className="home-action-content">
                      <div className="home-action-title">{item.title}</div>
                      <div className="home-action-subtitle">{item.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="home-right">
          {/* PTO Balance */}
          {ptoBalance && (
            <div className="home-card" style={{ cursor: 'pointer' }} onClick={() => { window.location.hash = 'pto'; }}>
              <h2 className="home-card-title">Time Off Balance</h2>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 32, fontWeight: 600, color: 'var(--symbolica-black)', lineHeight: 1 }}>
                  {ptoBalance.remaining}
                </span>
                <span style={{ fontSize: 13, color: 'var(--symbolica-muted)' }}>
                  days remaining
                </span>
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--symbolica-muted)' }}>
                <span>{ptoBalance.total} total</span>
                <span>{ptoBalance.used} used</span>
                {ptoBalance.pending > 0 && <span>{ptoBalance.pending} pending</span>}
              </div>
            </div>
          )}

          {/* Important Dates */}
          <div className="home-card">
            <h2 className="home-card-title">Important Dates</h2>
            {visibleDates.length === 0 ? (
              <p className="home-empty">No upcoming dates.</p>
            ) : (
              <div className="home-dates-list">
                {visibleDates.map((d, i) => (
                  <div className={`home-date-item${d.isHoliday ? ' home-date-holiday' : ''}`} key={i}>
                    <div className="home-date-badge">
                      <span className="home-date-month">{d.month}</span>
                      <span className="home-date-day">{d.day}</span>
                    </div>
                    <div className="home-date-content">
                      <div className="home-date-title">{d.title}</div>
                      <div className="home-date-subtitle">{d.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Tasks */}
          <div className="home-card">
            <h2 className="home-card-title">Quick Tasks</h2>
            <div className="home-quick-tasks">
              <button className="home-quick-btn" onClick={() => { window.location.hash = 'pto'; }}>
                Request Time Off
              </button>
              <button className="home-quick-btn" onClick={() => { window.location.hash = 'referrals'; }}>
                Submit a Referral
              </button>
              <button className="home-quick-btn" onClick={() => setShowReferenceModal(true)}>
                Request an Employment Reference
              </button>
              <button className="home-quick-btn" onClick={() => { window.location.hash = 'org'; }}>
                View Org Chart
              </button>
            </div>
          </div>
        </div>
      </div>

      {showReferenceModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="home-reference-title"
          onClick={() => setShowReferenceModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--symbolica-white)', borderRadius: 'var(--radius)',
              maxWidth: 520, width: '100%', padding: '28px 28px 24px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 id="home-reference-title" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 500, color: 'var(--symbolica-black)' }}>
                Need a Reference?
              </h3>
              <button
                onClick={() => setShowReferenceModal(false)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--symbolica-muted)' }}
              >
                &times;
              </button>
            </div>
            <p style={{ marginTop: 0, color: 'var(--symbolica-muted)' }}>
              Sometimes landlords or banks may request verification of employment. If you need this, please provide the following contact details and we will process it.
            </p>
            {referenceInfo === null ? (
              <div style={{ color: 'var(--symbolica-muted)', fontSize: 13 }}>Loading contact details&hellip;</div>
            ) : (
              <div style={{
                background: '#F0EDE6', borderRadius: 'var(--radius-sm)', padding: '18px 20px',
                display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14,
              }}>
                {referenceInfo.name && <div><strong>Name:</strong> {referenceInfo.name}</div>}
                {referenceInfo.title && <div><strong>Title:</strong> {referenceInfo.title}</div>}
                {referenceInfo.email && (
                  <div>
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${referenceInfo.email}`} style={{ color: 'var(--symbolica-periwinkle)' }}>
                      {referenceInfo.email}
                    </a>
                  </div>
                )}
                {referenceInfo.phone_uk && <div><strong>Phone (UK):</strong> {referenceInfo.phone_uk}</div>}
                {referenceInfo.phone_us && <div><strong>Phone (US / other):</strong> {referenceInfo.phone_us}</div>}
                {referenceInfo.company_house_number && (
                  <div style={{ marginTop: 4, fontSize: 12, color: 'var(--symbolica-muted)' }}>
                    UK Companies House Number: {referenceInfo.company_house_number}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
